'use client'
import { useEffect, useState } from 'react'

import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { getNonce } from '@src/utils/nonce.utils'

import styles from './referral-yotpo-widget.module.scss'

// Extracted so we don't suppress the no-literal-string lint rule for a control-character constant.
const ELEMENT_TYPE_SCRIPT = 'script'
const SCRIPT_ID_PREFIX = 'yotpo-referral-widget-loader'

export interface ReferralYotpoWidgetProps {
  /** Yotpo loader GUID. URL: https://cdn-widgetsrepository.yotpo.com/v1/loader/{loaderGuid} */
  loaderGuid?: string
  /** Yotpo widget instance id (rendered as `data-yotpo-instance-id`). */
  widgetInstanceId?: string
}

/**
 * Renders the standalone Yotpo referral widget (UK GDPR-compliant share buttons).
 *
 * Lifecycle:
 *  1. Mount → render a skeleton inside `.widgetSlot` so the user sees something while the loader downloads.
 *  2. Inject `<script src=".../loader/{guid}">` (deduped per-GUID so multiple instances coexist).
 *  3. On script load, call `window.yotpoWidgetsContainer.initWidgets()` — Yotpo hydrates the
 *     `<div class="yotpo-widget-instance">` and we hide the skeleton.
 *  4. On remount with the same script already present, immediately re-init and hide the skeleton on next tick.
 */
const ReferralYotpoWidget = ({ loaderGuid, widgetInstanceId }: ReferralYotpoWidgetProps) => {
  const [isWidgetReady, setIsWidgetReady] = useState(false)

  useEffect(() => {
    if (!isBrowser() || !loaderGuid) return undefined

    const scriptId = `${SCRIPT_ID_PREFIX}-${loaderGuid}`
    const existing = document.getElementById(scriptId)

    if (existing) {
      window.yotpoWidgetsContainer?.initWidgets?.()
      // initWidgets is synchronous; flip ready on the next tick so it has time to inject DOM.
      const t = window.setTimeout(() => {
        setIsWidgetReady(true)
      }, 0)
      return () => {
        window.clearTimeout(t)
      }
    }

    const script = document.createElement(ELEMENT_TYPE_SCRIPT)
    script.id = scriptId
    script.async = true
    script.src = `https://cdn-widgetsrepository.yotpo.com/v1/loader/${loaderGuid}`
    script.addEventListener('load', () => {
      setIsWidgetReady(true)
    })

    const nonce = getNonce()
    if (nonce) {
      script.setAttribute('nonce', nonce)
    }

    document.head.appendChild(script)
    return undefined
  }, [loaderGuid])

  if (!loaderGuid || !widgetInstanceId) return null

  return (
    <div className={styles.widgetContainer}>
      <div className={styles.widgetSlot}>
        {!isWidgetReady && (
          <div className={styles.skeleton} aria-busy="true" aria-live="polite" role="status">
            <div className={styles.skeletonLine} />
            <div className={styles.skeletonLine} />
            <div className={styles.skeletonBlock} />
          </div>
        )}
        <div className="yotpo-widget-instance" data-yotpo-instance-id={widgetInstanceId} />
      </div>
    </div>
  )
}

export default ReferralYotpoWidget
